import { hex, hsvToRgb, rgbToHsv, withAlpha, type Color } from "../../common/color.js";
import type { Insets, Point, Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { fillRings, strokeTriangles } from "../art/markup/shapes.js";

/** Interaction state a theme uses to pick colors for a control. */
export interface VisualState {
  hovered: boolean;
  pressed: boolean;
  focused: boolean;
  disabled: boolean;
  /** Checkboxes, radio buttons, toggle buttons. */
  checked?: boolean;
  /** Tri-state checkbox. */
  indeterminate?: boolean;
  /** List rows, list-view cells. */
  selected?: boolean;
  /** The owning window has focus. Selections dim when it does not. */
  active?: boolean;
  /** The dialog's default (Enter) button. */
  isDefault?: boolean;
}

export const DEFAULT_STATE: VisualState = {
  hovered: false,
  pressed: false,
  focused: false,
  disabled: false,
};

/**
 * How much frame a window wears, in the spirit of Win32's `FormBorderStyle`.
 *
 * - `sizable` — the theme's full frame: caption, structural border, and eight
 *   resize grips. The default.
 * - `single` — the same caption over a hairline border, and the window is a
 *   fixed size: no grips, no drag-to-resize, no maximize button.
 * - `none` — no chrome at all. The client area is the whole window, so
 *   whatever is inside it draws every pixel. There is nothing to grab, so a
 *   borderless window that wants to move calls `Window.startMove`.
 */
export type WindowBorderStyle = "sizable" | "single" | "none";

export interface WindowVisualState extends VisualState {
  /** The window is the active (foreground) one. */
  active: boolean;
  borderStyle: WindowBorderStyle;
  /**
   * The eight grips are live. Already folded together for the theme: a
   * `single` or `none` window is never resizable, whatever it was asked for.
   */
  resizable: boolean;
  showMinimize: boolean;
  showMaximize: boolean;
  showClose: boolean;
  maximized: boolean;
  /**
   * A tool window - WinForms' FixedToolWindow and SizableToolWindow: a palette or inspector
   * with a shorter caption and a close button only. `captionHeight` answers for it, so a theme
   * that sizes its decoration from that gets one for nothing.
   */
  toolWindow?: boolean;
  /**
   * The window is parked: it is being drawn as a minimized stub — a caption
   * bar and nothing else — rather than as a window.
   *
   * The rest of the state is already reconciled with it, so a theme that
   * does nothing with this flag still paints a correct stub: `bounds` is
   * stub-sized, the grips are off, and the control box is down to whichever
   * buttons a parked window still offers. It is here for a theme that wants
   * a parked window to *look* parked — a dimmer caption, a different tint.
   */
  minimized: boolean;
  /** Which caption button the pointer is over, if any. */
  hoveredButton?: CaptionButton | null;
  pressedButton?: CaptionButton | null;
  /**
   * 1 down to 0 while the frame flashes to say a click was refused, 0 the
   * rest of the time. Windows draws attention to the modal you have to deal
   * with first; this is the same idea.
   */
  flash?: number;
}

export type CaptionButton = "minimize" | "maximize" | "close";

/** What a theme needs to know to paint one tab. */
export interface TabVisualState extends VisualState {
  /** The tab whose page is showing. */
  selected: boolean;
  /** The strip sits under the page rather than above it. */
  bottom: boolean;
}

/** What a theme needs to know to paint a splitter bar. */
export interface SplitterVisualState extends VisualState {
  /** The bar itself runs top to bottom, so it moves left and right. */
  vertical: boolean;
  /** The user is dragging it right now. */
  dragging: boolean;
}

/** The pieces of a slider, in the control's own coordinates. */
export interface SliderParts {
  bounds: Rect;
  /** The channel the thumb runs along. */
  track: Rect;
  /** The part of the track behind the thumb. */
  filled: Rect;
  thumb: Rect;
  vertical: boolean;
  /**
   * Tick positions along the track, in local px on the moving axis. Empty
   * when the slider has no ticks.
   */
  ticks: readonly number[];
}

/** What a theme needs to know to paint a stat bar. */
export interface StatBarVisual {
  /** Filled proportion, 0..1. */
  fraction: number;
  /** Where the trailing "chip damage" fill has got to, 0..1. */
  chipFraction: number;
  /** Shield or overheal past the fill, as a proportion of the maximum. */
  shieldFraction: number;
  /** Notches across the bar; 0 or 1 for a continuous fill. */
  segments: number;
  vertical: boolean;
  /** The bar is low enough to be asking for attention. */
  low: boolean;
  disabled: boolean;
}

/** What a theme needs to know to paint an inventory or hotbar slot. */
export interface ItemSlotVisual extends VisualState {
  /** There is something in it. */
  filled: boolean;
  /** Not unlocked yet. */
  locked: boolean;
  /** Picked out — a legal drop target, or a match for a filter. */
  highlighted: boolean;
  /** The rarity color of what is in it, or null when empty. */
  rarity: Color | null;
}

/** One wedge of a radial menu. Angles are clockwise from 12 o'clock. */
export interface RadialWedgeVisual extends VisualState {
  center: Point;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  /** The item's own color, or null to take the theme's. */
  color: Color | null;
  /** The wedge the pointer or stick is on. */
  highlighted: boolean;
}

/**
 * One node of a skill or tech graph.
 *
 * The state is spelled out as booleans rather than as the gaming module's own
 * `NodeState`, so that `themes` keeps no dependency on `gaming` — the
 * dependency runs one way, and this is the seam where it would otherwise leak.
 */
export interface GraphNodeVisual extends VisualState {
  center: Point;
  radius: number;
  /** A prerequisite is missing. */
  locked: boolean;
  /** Some ranks taken, but not all. */
  partial: boolean;
  /** Every rank taken. */
  taken: boolean;
  color: Color | null;
}

/** One cell of a production queue. */
export interface QueueCellVisual extends VisualState {
  /** The job actually being worked on. */
  head: boolean;
}

/** One line of a dialogue choice list. */
export interface ChoiceRowVisual extends VisualState {
  /** The line the keyboard or pointer is on. */
  highlighted: boolean;
  /** Taken on a previous run through. */
  seen: boolean;
}

/** What a theme needs to know to paint a radial gauge's track. */
export interface GaugeTrackVisual {
  center: Point;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  /** The track color the control was given, or null for none. */
  trackColor: Color | null;
  /** A ring rather than a filled sweep. */
  ring: boolean;
}

/** What a theme needs to know to paint a busy spinner. */
export interface SpinnerVisual {
  /** Position around one full turn, 0..1. */
  phase: number;
  disabled: boolean;
}

/** What a theme needs to know to paint a progress bar. */
export interface ProgressVisual {
  /** Filled proportion, 0..1. Meaningless when `indeterminate`. */
  fraction: number;
  indeterminate: boolean;
  /** Sweep position of the marquee, 0..1, while indeterminate. */
  phase: number;
  disabled: boolean;
}

/** What a theme needs to know to paint a chart's plot area. */
export interface ChartPlotVisual {
  /**
   * Which way the gridlines run: `horizontal` (at a y each) for a chart whose values go up, and
   * `vertical` (at an x each) for one lying on its side, whose values go right.
   */
  lines: "horizontal" | "vertical";
  /** Where each value gridline is, local px: a y for horizontal lines, an x for vertical ones. */
  gridLines: readonly number[];
  /**
   * Gridlines the other way, at the ticks of a numeric or time x axis: an x each when `lines` is
   * horizontal, a y each when it is vertical. Absent or empty on a category axis.
   */
  crossLines?: readonly number[];
  /** Where the zero line is when zero is inside the range, else null. */
  baseline: number | null;
  showGrid: boolean;
  disabled: boolean;
}

export interface ScrollBarParts {
  /** Whole control, local coords. */
  bounds: Rect;
  track: Rect;
  thumb: Rect;
  decrease: Rect | null;
  increase: Rect | null;
  vertical: boolean;
  hoveredPart: ScrollBarPart | null;
  pressedPart: ScrollBarPart | null;
}

export type ScrollBarPart = "thumb" | "decrease" | "increase" | "pageUp" | "pageDown";

export interface ThemeMetrics {
  /** Window chrome. */
  titleBarHeight: number;
  windowBorder: number;
  windowCornerRadius: number;
  resizeGripSize: number;
  captionButtonWidth: number;
  windowPadding: Insets;

  /** Generic control chrome. */
  controlCornerRadius: number;
  controlHeight: number;
  buttonPadding: Insets;
  buttonMinWidth: number;
  focusRingWidth: number;
  focusRingInset: number;

  /** Text fields. */
  textBoxPadding: Insets;

  /** Check / radio. */
  checkBoxSize: number;
  radioSize: number;
  checkLabelGap: number;

  /** Splitters and busy spinners. */
  splitterSize: number;
  spinnerSize: number;

  /** Sliders. */
  sliderThumbSize: number;
  sliderTrackThickness: number;
  /** Width of one of a NumericUpDown's stepper buttons. */
  spinButtonWidth: number;

  /** Scrolling. */
  scrollBarSize: number;
  scrollBarMinThumb: number;
  /** Classic shows stepper arrows; Modern does not. */
  scrollBarArrows: boolean;

  /** Lists. */
  listItemHeight: number;
  listPadding: Insets;
  headerHeight: number;
  columnPadding: number;

  /** Dialogs. */
  dialogPadding: number;
  dialogButtonGap: number;

  font: FontSpec;
  captionFont: FontSpec;
  monoFont: FontSpec;
}

export interface ThemePalette {
  desktop: Color;

  windowBg: Color;
  windowBorderActive: Color;
  windowBorderInactive: Color;
  titleBarActive: Color;
  titleBarInactive: Color;
  titleTextActive: Color;
  titleTextInactive: Color;

  controlBg: Color;
  controlBgHover: Color;
  controlBgPressed: Color;
  controlBgDisabled: Color;
  controlBorder: Color;
  controlBorderHover: Color;

  fieldBg: Color;
  fieldBgDisabled: Color;
  fieldBorder: Color;
  fieldBorderFocused: Color;

  text: Color;
  textDisabled: Color;
  textSubtle: Color;
  /** Caption on an ordinary (non-default) button. */
  buttonText: Color;
  /** What is typed into a text box, text area, combo or date field. */
  fieldText: Color;
  /** A field's watermark: the placeholder shown while it is empty. */
  placeholderText: Color;
  /**
   * Text set as a title or heading. Webwin's own controls do not read it; it is
   * here so an application that distinguishes headings has a palette entry a
   * theme can answer for, rather than reaching for `text`.
   */
  headingText: Color;

  accent: Color;
  accentHover: Color;
  accentPressed: Color;
  accentText: Color;

  selectionBg: Color;
  selectionText: Color;
  selectionInactiveBg: Color;
  /** Text selection inside a TextBox. */
  textSelectionBg: Color;

  scrollTrack: Color;
  scrollThumb: Color;
  scrollThumbHover: Color;
  scrollThumbPressed: Color;

  headerBg: Color;
  headerBgHover: Color;
  headerText: Color;
  gridLine: Color;

  focusRing: Color;
  shadow: Color;

  /** Classic 3D bevel ramp; Modern themes may leave these unused. */
  bevelHighlight: Color;
  bevelLight: Color;
  bevelShadow: Color;
  bevelDarkShadow: Color;
}

/**
 * A theme owns both the metrics controls lay themselves out with and the
 * routines that paint their chrome. Controls never branch on which theme is
 * active — they call `theme.drawButton(...)` and then draw their own content.
 */
export abstract class Theme {
  abstract readonly name: string;
  abstract readonly metrics: ThemeMetrics;
  abstract readonly palette: ThemePalette;
  /** True for dark palettes; controls use it to pick icon polarity. */
  abstract readonly isDark: boolean;

  // --- window chrome ----------------------------------------------------

  /**
   * Paint the frame: border, caption, and caption buttons.
   *
   * Only ever called for a window that has chrome. A `none` window goes to
   * `drawBorderlessFrame` instead, so an implementation here never has to
   * consider the case where there is nothing to draw.
   */
  abstract drawWindowFrame(rc: RenderContext, bounds: Rect, s: WindowVisualState): void;
  /**
   * Client area in window-local coords, given the window's outer size.
   *
   * As with `drawWindowFrame`, a `none` window never reaches this — its
   * client area is the whole window by definition.
   */
  abstract windowClientRect(bounds: Rect, s: WindowVisualState): Rect;
  /**
   * Caption button rects in window-local coords, right to left.
   *
   * Return null for a button `s` says is hidden. The `showMinimize` /
   * `showMaximize` / `showClose` flags arrive already reconciled with the
   * border style, so honoring them is all a theme has to do.
   */
  abstract captionButtonRect(
    bounds: Rect,
    button: CaptionButton,
    s: WindowVisualState,
  ): Rect | null;

  /**
   * How thick the frame is around a window in this state.
   *
   * Themes ask this rather than reading `metrics.windowBorder`, because a
   * `single` window wears a hairline instead of the theme's structural
   * border — Win32's FixedSingle look, where a fixed window is visibly
   * thinner-framed than a sizable one. A theme whose border is already a
   * hairline (or nothing at all) is left alone.
   */
  frameBorder(s: WindowVisualState): number {
    const b = this.metrics.windowBorder;
    if (s.borderStyle === "none") return 0;
    return s.borderStyle === "single" ? Math.min(1, b) : b;
  }

  /** Caption height in this state — zero for a window with no chrome, shorter for a tool window. */
  captionHeight(s: WindowVisualState): number {
    if (s.borderStyle === "none") return 0;
    const h = this.metrics.titleBarHeight;
    return s.toolWindow ? Math.max(16, Math.round(h * 0.75)) : h;
  }

  /**
   * The whole "frame" of a `none` window: the ground its contents sit on.
   *
   * Virtual with a working default — a flat fill of the window color, square
   * to the pixel, because a borderless window exists so that something else
   * can own every pixel of it. A theme that wants its own answer (a glass
   * pane, a hairline outline) overrides this; most should not.
   */
  drawBorderlessFrame(rc: RenderContext, bounds: Rect, _s: WindowVisualState): void {
    rc.fillRect({ x: 0, y: 0, width: bounds.width, height: bounds.height }, this.palette.windowBg);
  }

  /**
   * Chrome in a shape of the control's own (`Control.shape`): `rings` is the outline in local
   * pixels, holes included. Virtual with a working default - the button or panel colors filling
   * the outline, a one-pixel edge round it, and the focus ring following the edge - so every theme
   * draws a hexagonal button without knowing hexagons exist. A theme with its own idea of a shaped
   * control overrides this. `fill` replaces the ground color, as a panel's `background` does.
   */
  drawShapedChrome(rc: RenderContext, rings: readonly (readonly Point[])[], s: VisualState, kind: "button" | "panel", fill?: Color | null): void {
    const p = this.palette;
    const ground =
      fill ??
      (kind === "panel"
        ? p.windowBg
        : s.disabled
          ? p.controlBgDisabled
          : s.pressed
            ? p.controlBgPressed
            : s.hovered
              ? p.controlBgHover
              : p.controlBg);
    rc.fillTriangles(fillRings(rings), ground);
    const edge = kind === "button" && s.hovered && !s.disabled ? p.controlBorderHover : p.controlBorder;
    for (const ring of rings) rc.fillTriangles(strokeTriangles(ring, true, 1), edge);
    if (kind === "button" && s.focused && !s.disabled) {
      for (const ring of rings) rc.fillTriangles(strokeTriangles(ring, true, this.metrics.focusRingWidth), p.focusRing);
    }
  }

  // --- controls ---------------------------------------------------------

  abstract drawButton(rc: RenderContext, r: Rect, s: VisualState): void;
  /** Text color a button's caption should use in this state. */
  abstract buttonTextColor(s: VisualState): Color;
  abstract drawTextBox(rc: RenderContext, r: Rect, s: VisualState): void;
  abstract drawCheckBox(rc: RenderContext, r: Rect, s: VisualState): void;
  abstract drawRadioButton(rc: RenderContext, r: Rect, s: VisualState): void;
  abstract drawPanel(rc: RenderContext, r: Rect, sunken: boolean): void;
  abstract drawScrollBar(rc: RenderContext, parts: ScrollBarParts, s: VisualState): void;
  abstract drawListBackground(rc: RenderContext, r: Rect, s: VisualState): void;
  abstract drawListItem(rc: RenderContext, r: Rect, s: VisualState): void;
  abstract drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void;
  abstract drawFocusRing(rc: RenderContext, r: Rect): void;

  /**
   * Where the window's caption text goes, in window-local coordinates.
   *
   * Themes that decorate the title bar — a rail, greeble bars, rivets — move
   * the text out of the way by overriding `titleTextInset`, or this method
   * outright when the layout is more involved than a left inset.
   */
  titleTextRect(bounds: Rect, s: WindowVisualState): Rect {
    const b = this.frameBorder(s);
    let rightmost = b + Math.max(0, bounds.width - b * 2);
    for (const button of ["minimize", "maximize", "close"] as CaptionButton[]) {
      const r = this.captionButtonRect(bounds, button, s);
      if (r) rightmost = Math.min(rightmost, r.x);
    }
    const left = b + this.titleTextInset;
    return {
      x: left,
      y: b,
      width: Math.max(0, rightmost - left - 6),
      height: this.captionHeight(s),
    };
  }

  /** Left gap between the frame edge and the caption text. */
  protected get titleTextInset(): number {
    return 10;
  }

  /**
   * One tab in a tab strip.
   *
   * The selected tab is expected to merge into the page below it, so a theme
   * draws no border along the shared edge; the control paints over that seam
   * afterwards to be certain.
   */
  drawTab(rc: RenderContext, r: Rect, s: TabVisualState): void {
    const p = this.palette;
    const radius = Math.min(6, this.metrics.controlCornerRadius + 2);
    const fill = s.selected
      ? p.windowBg
      : s.hovered && !s.disabled
        ? p.controlBgHover
        : p.controlBg;
    rc.drawRect(r, {
      fill,
      radius,
      borderWidth: 1,
      borderColor: s.selected ? p.controlBorder : p.gridLine,
    });
    // Square off the edge that meets the page so the two read as one surface.
    const seam = s.bottom ? r.y : r.y + r.height - radius;
    rc.fillRect({ x: r.x + 1, y: seam, width: r.width - 2, height: radius }, fill);
  }

  tabTextColor(s: TabVisualState): Color {
    if (s.disabled) return this.palette.textDisabled;
    // The selected tab has merged into the page, so it takes page ink. An
    // unselected one still sits on the control surface, and a theme may have
    // made that surface anything at all — LCARS paints it saturated amber —
    // so ask the same question a button asks.
    return s.selected ? this.palette.text : this.buttonTextColor(s);
  }

  /**
   * Drawn over the frame while a modal is refusing a click.
   *
   * Virtual with a working default: a wash of the accent color across the
   * caption, fading as the flash runs out. `Window` used to do this by
   * forcing `active` true for a moment — which does nothing at all, because a
   * modal is the active window already.
   *
   * `bounds` is the frame rect as `drawWindowFrame` receives it: only its
   * width and height mean anything, the origin is the window's own space.
   */
  /**
   * Where a window being dragged to an edge will land if it is let go now.
   *
   * Virtual with a working default: a translucent accent wash with an outline, the shape
   * Windows draws. `r` is in desktop coordinates.
   */
  drawSnapPreview(rc: RenderContext, r: Rect): void {
    const radius = this.metrics.windowCornerRadius;
    rc.fillRect(r, withAlpha(this.palette.accent, 0.18), radius);
    rc.strokeRect(r, withAlpha(this.palette.accent, 0.7), 2);
  }

  drawWindowFlash(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const t = s.flash ?? 0;
    if (t <= 0) return;
    const m = this.metrics;
    const ink = withAlpha(this.palette.accent, 0.6 * t);
    const radius = s.maximized ? 0 : m.windowCornerRadius;
    const height = this.frameBorder(s) + this.captionHeight(s);
    // A borderless window has no caption to wash, so the whole outline
    // pulses instead. Refusing a click has to be visible either way.
    if (height <= 0) {
      rc.strokeRect({ x: 0, y: 0, width: bounds.width, height: bounds.height }, ink, 3);
      return;
    }
    rc.fillRect({ x: 0, y: 0, width: bounds.width, height: Math.min(bounds.height, height) }, ink, radius);
  }

  /**
   * One position dot under a carousel.
   *
   * Virtual with a working default: a filled pill for the page showing, a
   * hollow one for the rest. A theme that wants bars or numbers overrides it.
   */
  drawCarouselDot(rc: RenderContext, r: Rect, active: boolean, hovered: boolean): void {
    const p = this.palette;
    const radius = Math.min(r.width, r.height) / 2;
    if (active) {
      rc.fillRect(r, p.accent, radius);
      return;
    }
    rc.drawRect(r, {
      fill: hovered ? p.controlBgHover : p.controlBg,
      borderWidth: 1,
      borderColor: hovered ? p.controlBorderHover : p.controlBorder,
      radius,
    });
  }

  /**
   * The draggable bar between two panes.
   *
   * Deliberately quiet at rest — a Windows splitter is a gap, not a widget —
   * and shows a grip only once the pointer is on it.
   */
  drawSplitter(rc: RenderContext, r: Rect, s: SplitterVisualState): void {
    const p = this.palette;
    if (s.dragging) rc.fillRect(r, p.accent);
    else if (s.hovered) rc.fillRect(r, p.controlBgHover);
    else rc.fillRect(r, p.controlBg);

    // A hairline down the middle keeps the seam readable when the two panes
    // are the same color as the bar.
    const ink = s.dragging ? p.accentText : s.focused ? p.focusRing : p.gridLine;
    if (s.vertical) rc.vLine(Math.round(r.x + r.width / 2), r.y, r.height, ink);
    else rc.hLine(r.x, Math.round(r.y + r.height / 2), r.width, ink);

    // A focus ring round a 6px bar is all ring, so focus shows as the grip
    // instead: the same dots the pointer brings up.
    if (!s.hovered && !s.dragging && !s.focused) return;
    // Three grip dots at the center, along the bar.
    const dot = 2;
    const gap = 3;
    const span = dot * 3 + gap * 2;
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;
    const grip = s.dragging ? p.accentText : s.focused && !s.hovered ? p.focusRing : p.textSubtle;
    for (let i = 0; i < 3; i++) {
      const offset = -span / 2 + i * (dot + gap);
      const box = s.vertical
        ? { x: cx - dot / 2, y: cy + offset, width: dot, height: dot }
        : { x: cx + offset, y: cy - dot / 2, width: dot, height: dot };
      rc.fillRect(box, grip, dot / 2);
    }
  }

  /**
   * A slider: its channel, the filled part behind the thumb, and the thumb.
   *
   * Virtual with a working default, so a new theme gets a usable slider
   * without writing one. The filled portion is drawn in the accent color
   * because that is what says "this much" at a glance on a settings screen.
   */
  drawSlider(rc: RenderContext, parts: SliderParts, s: VisualState): void {
    const p = this.palette;
    const { track, filled, thumb } = parts;
    const trackRadius = Math.min(track.width, track.height) / 2;

    for (const tick of parts.ticks) {
      const length = 5;
      const box = parts.vertical
        ? { x: track.x + track.width + 4, y: tick - 0.5, width: length, height: 1 }
        : { x: tick - 0.5, y: track.y + track.height + 4, width: 1, height: length };
      rc.fillRect(box, s.disabled ? p.textDisabled : p.textSubtle);
    }

    rc.fillRect(track, s.disabled ? p.controlBgDisabled : p.scrollTrack, trackRadius);
    if (filled.width > 0 && filled.height > 0) {
      rc.fillRect(filled, s.disabled ? p.textDisabled : p.accent, trackRadius);
    }

    const fill = s.disabled
      ? p.controlBgDisabled
      : s.pressed
        ? p.accentPressed
        : s.hovered
          ? p.accentHover
          : p.accent;
    rc.drawRect(thumb, {
      fill,
      radius: Math.min(thumb.width, thumb.height) / 2,
      borderWidth: 1,
      borderColor: s.disabled ? p.controlBorder : p.accentText,
    });
    if (s.focused && !s.disabled) this.drawFocusRing(rc, parts.bounds);
  }

  // --- game chrome --------------------------------------------------------
  //
  // Virtual with working defaults, like the tab and progress hooks above, so
  // the eleven existing themes need no edits at all and a game-flavored one
  // — Rpg, Dungeon, SciFi — can override just the pieces it cares about.

  /**
   * The frame round a stat bar: everything except the fills, which the
   * control draws itself because it owns the animation.
   *
   * Split that way deliberately. A theme that wanted to draw the fills too
   * would have to be handed the chip animation's state and the pulse phase,
   * and every theme would then have to reimplement both to look right.
   */
  drawStatBarFrame(rc: RenderContext, r: Rect, s: StatBarVisual): void {
    const p = this.palette;
    const radius = Math.min(this.metrics.controlCornerRadius, Math.min(r.width, r.height) / 2);
    rc.drawRect(r, {
      fill: p.controlBgDisabled,
      radius,
      borderWidth: 1,
      borderColor: s.low && !s.disabled ? p.accent : p.controlBorder,
    });
  }

  /**
   * The empty well of an inventory or hotbar slot, under whatever is in it.
   *
   * A stone recess in Dungeon, a tooled leather square in Rpg, a lit outline
   * in SciFi — this is the hook that makes an inventory look like it belongs
   * to the game rather than to a settings dialog.
   */
  drawItemSlot(rc: RenderContext, r: Rect, s: ItemSlotVisual): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.locked ? p.controlBgDisabled : s.hovered && !s.disabled ? p.controlBgHover : p.fieldBg,
      radius: this.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: s.highlighted ? p.accent : s.rarity ?? p.controlBorder,
    });
  }

  /**
   * The panel behind a line of dialogue.
   *
   * Nearly opaque rather than solid: a visual novel's box sits over art, and
   * a little of the scene showing through is most of what makes it read as
   * part of the game.
   */
  drawDialogueBox(rc: RenderContext, r: Rect): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: withAlpha(p.windowBg, 0.94),
      radius: this.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });
  }

  /**
   * One wedge of a radial menu.
   *
   * The label and the glyph stay with the control, which knows where they go;
   * this is the wedge behind them.
   */
  drawRadialWedge(rc: RenderContext, w: RadialWedgeVisual): void {
    const p = this.palette;
    const base = w.color ?? p.controlBg;
    const fill = w.disabled
      ? withAlpha(p.controlBgDisabled, 0.8)
      : w.highlighted
        ? p.accent
        : withAlpha(base, 0.9);
    rc.fillArc(w.center, w.innerRadius, w.outerRadius, w.startAngle, w.endAngle, fill);
  }

  /** The ink for a radial wedge's label and glyph. */
  radialWedgeTextColor(w: RadialWedgeVisual): Color {
    if (w.disabled) return this.palette.textDisabled;
    return w.highlighted ? this.palette.accentText : this.palette.text;
  }

  /** One node of a skill or tech graph: the body and its rim. */
  drawGraphNode(rc: RenderContext, n: GraphNodeVisual): void {
    const p = this.palette;
    const base = n.color ?? p.accent;
    const fill = n.locked
      ? p.controlBgDisabled
      : n.taken
        ? base
        : n.partial
          ? withAlpha(base, 0.45)
          : p.controlBg;
    const rim = n.locked ? p.controlBorder : n.hovered ? p.accentHover : withAlpha(base, 0.9);
    rc.fillArc(n.center, 0, n.radius, 0, Math.PI * 2, fill);
    rc.fillArc(n.center, n.radius - 1.5, n.radius, 0, Math.PI * 2, rim);
  }

  /** The ink for a graph node's glyph. */
  graphNodeTextColor(n: GraphNodeVisual): Color {
    if (n.locked) return this.palette.textDisabled;
    return n.taken ? this.palette.accentText : this.palette.text;
  }

  /** A prerequisite line between two graph nodes. */
  drawGraphEdge(rc: RenderContext, from: Point, to: Point, thickness: number, met: boolean): void {
    const p = this.palette;
    rc.drawLine(from, to, thickness, withAlpha(met ? p.accent : p.gridLine, 0.8));
  }

  /** A rank pip round the edge of a graph node. */
  drawGraphPip(
    rc: RenderContext,
    at: Point,
    radius: number,
    filled: boolean,
    color: Color | null,
  ): void {
    const ink = filled ? color ?? this.palette.accent : this.palette.gridLine;
    rc.fillArc(at, 0, radius, 0, Math.PI * 2, ink);
  }

  /** One cell of a production queue. */
  drawQueueCell(rc: RenderContext, r: Rect, s: QueueCellVisual): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: s.hovered && !s.disabled ? p.controlBgHover : p.fieldBg,
      radius: this.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: s.head ? p.accent : p.controlBorder,
    });
  }

  /**
   * One line of a dialogue choice list.
   *
   * A wash rather than a solid fill by default: a choice list usually sits
   * over a scene, and a solid row would punch a hole in it.
   */
  drawChoiceRow(rc: RenderContext, r: Rect, s: ChoiceRowVisual): void {
    if (!s.highlighted || s.disabled) return;
    rc.fillRect(r, withAlpha(this.palette.accent, 0.22), this.metrics.controlCornerRadius);
  }

  /** The ink for one line of a choice list. */
  choiceRowTextColor(s: ChoiceRowVisual): Color {
    const p = this.palette;
    if (s.disabled) return p.textDisabled;
    if (s.seen) return p.textSubtle;
    return s.highlighted ? p.text : p.textSubtle;
  }

  /** The unfilled part of a radial gauge, behind its value. */
  drawGaugeTrack(rc: RenderContext, g: GaugeTrackVisual): void {
    if (!g.trackColor) return;
    rc.fillArc(g.center, g.innerRadius, g.outerRadius, g.startAngle, g.endAngle, g.trackColor);
  }

  /** The wash over a hotbar while every slot is locked out. */
  drawGlobalCooldown(rc: RenderContext, r: Rect, fraction: number): void {
    const dim = this.palette.desktop;
    rc.fillRect(
      r,
      { r: dim.r, g: dim.g, b: dim.b, a: 0.35 * fraction },
      this.metrics.controlCornerRadius,
    );
  }

  /**
   * The tick beside a completed quest objective.
   *
   * Drawn from primitives rather than through the glyph registry: that module
   * pulls in `ModernTheme` for its checkmark, so a theme reaching for it here
   * would be importing itself.
   */
  drawObjectiveMark(rc: RenderContext, box: Rect, ink: Color): void {
    const w = box.width;
    const h = box.height;
    const stroke = Math.max(1.2, Math.min(w, h) * 0.16);
    rc.drawLine(
      { x: box.x + w * 0.16, y: box.y + h * 0.52 },
      { x: box.x + w * 0.42, y: box.y + h * 0.78 },
      stroke,
      ink,
    );
    rc.drawLine(
      { x: box.x + w * 0.42, y: box.y + h * 0.78 },
      { x: box.x + w * 0.86, y: box.y + h * 0.22 },
      stroke,
      ink,
    );
  }

  /**
   * One piece of floating combat text.
   *
   * Virtual so a theme can outline it. Damage numbers land over whatever the
   * game happens to be drawing, and an unoutlined number over a bright
   * explosion is unreadable — but an outline costs four extra draws a number,
   * so it stays off unless a theme asks for it.
   */
  drawFloatingText(
    rc: RenderContext,
    text: string,
    x: number,
    y: number,
    font: FontSpec,
    color: Color,
  ): void {
    rc.drawText(text, x, y, font, color, { baseline: "middle" });
  }

  /**
   * Behind a nameplate's caption. Bare by default — most games want the name
   * floating free — but this is where a legibility scrim goes.
   */
  drawNameplateFrame(_rc: RenderContext, _r: Rect, _s: VisualState): void {}

  /** Behind a resource strip. Bare by default. */
  drawResourceBackground(_rc: RenderContext, _r: Rect): void {}

  /**
   * A busy spinner filling `r`.
   *
   * Every transform in this renderer is a translate plus a uniform scale, so
   * there is no rotation to lean on: the default is a ring of dots whose
   * brightness chases the phase, which reads as motion without any.
   */
  drawSpinner(rc: RenderContext, r: Rect, s: SpinnerVisual): void {
    const p = this.palette;
    const count = 8;
    const size = Math.min(r.width, r.height);
    if (size <= 0) return;
    const dot = Math.max(2, Math.round(size * 0.18));
    const radius = (size - dot) / 2;
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;
    const ink = s.disabled ? p.textDisabled : p.accent;
    const head = s.phase * count;

    for (let i = 0; i < count; i++) {
      // Distance behind the head, 0 at the head and growing round the ring.
      const behind = (((head - i) % count) + count) % count;
      const alpha = 0.15 + 0.85 * (1 - behind / count);
      const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
      rc.fillRect(
        {
          x: cx + Math.cos(angle) * radius - dot / 2,
          y: cy + Math.sin(angle) * radius - dot / 2,
          width: dot,
          height: dot,
        },
        withAlpha(ink, alpha),
        dot / 2,
      );
    }
  }

  /**
   * The scrim a busy container lays over its own content.
   *
   * Washed out rather than opaque, so the user can still see what is being
   * worked on and does not lose their place.
   */
  drawBusyOverlay(rc: RenderContext, r: Rect): void {
    rc.fillRect(r, withAlpha(this.palette.windowBg, 0.72));
  }

  /**
   * A horizontal progress bar: track and fill together.
   *
   * Virtual with a working default, so no theme is forced to implement it —
   * but Classic does, because a Win95 progress bar is chunky segments rather
   * than a continuous fill.
   */
  drawProgressBar(rc: RenderContext, r: Rect, p: ProgressVisual): void {
    const pal = this.palette;
    const radius = this.metrics.controlCornerRadius;
    rc.drawRect(r, {
      fill: pal.fieldBg,
      radius,
      borderWidth: 1,
      borderColor: pal.fieldBorder,
    });

    const inner: Rect = { x: r.x + 1, y: r.y + 1, width: r.width - 2, height: r.height - 2 };
    if (inner.width <= 0 || inner.height <= 0) return;
    const ink = p.disabled ? pal.controlBgDisabled : pal.accent;
    const innerRadius = Math.max(0, radius - 1);

    if (p.indeterminate) {
      // A block that slides in from the left and out to the right, so the
      // sweep reads as continuous rather than snapping back.
      const blockW = Math.max(16, inner.width * 0.3);
      const travel = inner.width + blockW;
      const x = inner.x - blockW + p.phase * travel;
      const x0 = Math.max(inner.x, x);
      const x1 = Math.min(inner.x + inner.width, x + blockW);
      if (x1 > x0) {
        rc.fillRect({ x: x0, y: inner.y, width: x1 - x0, height: inner.height }, ink, innerRadius);
      }
      return;
    }

    const w = Math.round(inner.width * Math.min(1, Math.max(0, p.fraction)));
    if (w > 0) rc.fillRect({ ...inner, width: w }, ink, innerRadius);
  }

  /**
   * The color of a chart's `index`th series.
   *
   * Virtual with a working default: the accent first, then hues stepped round the wheel by the
   * golden angle from it, so neighbors never sit close together however many series there are
   * and every theme's chart already looks like it belongs to that theme. Saturation and value are
   * held in a band that reads against both light and dark grounds.
   */
  chartSeriesColor(index: number): Color {
    const base = rgbToHsv(this.palette.accent);
    if (index === 0) return this.palette.accent;
    const h = (((base.h + index * 137.508) % 360) + 360) % 360;
    const s = Math.min(0.85, Math.max(0.45, base.s));
    const v = this.isDark ? Math.max(0.75, base.v) : Math.min(0.8, Math.max(0.55, base.v));
    return hsvToRgb(h, s, v);
  }

  /**
   * The box a chart's crosshair reads its value out in, on an axis: the price under the pointer on
   * the value axis, the date on the category axis.
   *
   * Virtual with a working default - the text color as a solid ground, so the readout stands out
   * of the axis labels it sits among - with `chartReadoutTextColor` for what is written on it.
   */
  drawChartReadout(rc: RenderContext, r: Rect): void {
    rc.fillRect(r, this.palette.text, Math.min(3, this.metrics.controlCornerRadius));
  }

  chartReadoutTextColor(): Color {
    return this.palette.windowBg;
  }

  /**
   * The color of a rising (`true`) or falling price on a financial chart - a candle or an OHLC bar.
   *
   * Virtual with a working default: the green and red traders read without thinking, a shade
   * deeper on a light ground and a shade brighter on a dark one. A theme whose palette fights
   * them overrides this; a chart can also set its own per instance.
   */
  chartTrendColor(rising: boolean): Color {
    if (rising) return hex(this.isDark ? "#3fb950" : "#1a7f37");
    return hex(this.isDark ? "#f85149" : "#cf222e");
  }

  /**
   * A chart's plot area: the value gridlines and the zero line. The series are the chart's own
   * to draw on top.
   *
   * Virtual with a working default - hairline gridlines in the grid color and a firmer zero line -
   * so a theme with an opinion (dotted Win95 lines, a glowing sci-fi baseline) overrides one method.
   */
  drawChartPlot(rc: RenderContext, r: Rect, v: ChartPlotVisual): void {
    const p = this.palette;
    const axis = v.disabled ? p.textDisabled : p.controlBorder;
    if (v.lines === "vertical") {
      if (v.showGrid) {
        for (const x of v.gridLines) rc.vLine(Math.round(x), r.y, r.height, p.gridLine);
        for (const y of v.crossLines ?? []) rc.hLine(r.x, Math.round(y), r.width, p.gridLine);
      }
      rc.vLine(Math.round(v.baseline ?? r.x), r.y, r.height, axis);
      return;
    }
    if (v.showGrid) {
      for (const y of v.gridLines) rc.hLine(r.x, Math.round(y), r.width, p.gridLine);
      for (const x of v.crossLines ?? []) rc.vLine(Math.round(x), r.y, r.height, p.gridLine);
    }
    rc.hLine(r.x, Math.round(v.baseline ?? r.y + r.height), r.width, axis);
  }

  /**
   * The tooltip surface. Virtual rather than abstract: every theme gets a
   * usable default, and only one with an opinion about tooltips overrides it.
   */
  /**
   * The navigation index's surface.
   *
   * Virtual with a working default, like every other piece of chrome added
   * since: all ten themes get a panel none of them had to write, and a theme
   * with an opinion about floating surfaces overrides one method instead of
   * ten. The rows on top of it are drawn with `drawListItem`, which every
   * theme already implements.
   */
  drawNavigationIndexFrame(rc: RenderContext, r: Rect): void {
    this.drawTooltip(rc, r);
  }

  drawTooltip(rc: RenderContext, r: Rect): void {
    const p = this.palette;
    rc.drawRect(r, {
      fill: p.controlBg,
      radius: Math.min(4, this.metrics.controlCornerRadius),
      borderWidth: 1,
      borderColor: p.controlBorder,
    });
  }

  tooltipTextColor(): Color {
    return this.palette.text;
  }

  /** Ink color for a list row, which flips when the row is selected. */
  listItemTextColor(s: VisualState): Color {
    if (s.disabled) return this.palette.textDisabled;
    if (s.selected && s.active !== false) return this.palette.selectionText;
    return this.palette.text;
  }
}
